/**
 * The installable app: service worker registration, install onboarding, push subscription.
 * The worker itself is public/sw.js (plain JS, served from the root so its scope is the app).
 */
import { create } from 'zustand';
import type { AggregateState } from '@orgus/protocol';
import { ApiError } from '../../lib/api.ts';
import { isInstalledApp } from '../../lib/installedApp.ts';
import type { Entities } from '../../lib/store.ts';
import { subscribeDevice, unsubscribeDevice } from '../notify/api.ts';

export type Platform = 'ios' | 'android' | 'desktop';

export function detectPlatform(ua: string, maxTouchPoints = 0): Platform {
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  // iPadOS reports itself as a Mac; touch points give it away.
  if (/Macintosh/.test(ua) && maxTouchPoints > 1) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

/**
 * What stands between this device and push notifications, in the order a person has to
 * fix it: a secure origin, then (iOS) installing to the home screen, then browser support,
 * then permission.
 */
export type PushReadiness = 'insecure' | 'install-first' | 'unsupported' | 'denied' | 'ready' | 'on';

export interface PushEnv {
  secure: boolean;
  platform: Platform;
  standalone: boolean;
  hasServiceWorker: boolean;
  hasPush: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
}

export function pushReadiness(e: PushEnv): PushReadiness {
  if (!e.secure) return 'insecure';
  // iOS and iPadOS only offer Web Push to an app added to the home screen (16.4 or later).
  if (e.platform === 'ios' && !e.standalone) return 'install-first';
  if (!e.hasServiceWorker || !e.hasPush || e.permission === 'unsupported') return 'unsupported';
  if (e.permission === 'denied') return 'denied';
  return e.subscribed ? 'on' : 'ready';
}

export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Installed, with the system title bar or drawing its own (Window Controls Overlay). */
export const isStandalone = isInstalledApp;

/** A short label for this device, sent with the subscription so Settings can tell devices apart. */
export function deviceName(ua: string, platform: Platform): string {
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\/|FxiOS/.test(ua) ? 'Firefox' : /Chrome\/|CriOS/.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = platform === 'ios' ? (/iPad/.test(ua) || /Macintosh/.test(ua) ? 'iPad' : 'iPhone') : platform === 'android' ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'Mac' : 'Linux';
  return `${browser} on ${os}`;
}

// ---------------------------------------------------------------------------
// Live device state
// ---------------------------------------------------------------------------

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PwaStore {
  registration: ServiceWorkerRegistration | null;
  installPrompt: BeforeInstallPromptEvent | null;
  standalone: boolean;
  endpoint: string | null;
  permission: NotificationPermission | 'unsupported';
  /** Set by the worker when the page loaded from cache with no network. */
  offline: boolean;
}

export const usePwa = create<PwaStore>()(() => ({
  registration: null,
  installPrompt: null,
  standalone: false,
  endpoint: null,
  permission: typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  offline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
}));

export function pushEnv(): PushEnv {
  const s = usePwa.getState();
  return {
    secure: typeof window !== 'undefined' && window.isSecureContext,
    platform: detectPlatform(navigator.userAgent, navigator.maxTouchPoints),
    standalone: s.standalone,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasPush: typeof window !== 'undefined' && 'PushManager' in window,
    permission: s.permission,
    subscribed: !!s.endpoint,
  };
}

let started = false;

/**
 * Register the service worker (production builds, or any build with localStorage
 * 'orgus.sw' = '1') and watch install and connectivity state. Never in demo mode.
 */
export function registerPwa(opts: { mock: boolean }): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  usePwa.setState({ standalone: isStandalone() });
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    usePwa.setState({ installPrompt: e as BeforeInstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => usePwa.setState({ installPrompt: null, standalone: isStandalone() }));
  window.addEventListener('online', () => {
    usePwa.setState({ offline: false });
    navigator.serviceWorker?.controller?.postMessage({ type: 'flush' });
  });
  window.addEventListener('offline', () => usePwa.setState({ offline: true }));

  let wanted = import.meta.env.PROD;
  try {
    if (localStorage.getItem('orgus.sw') === '1') wanted = true;
    if (localStorage.getItem('orgus.sw') === '0') wanted = false;
  } catch {
    // Storage blocked: keep the default.
  }
  if (opts.mock || !wanted || !('serviceWorker' in navigator) || !window.isSecureContext) return;
  navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then(async (registration) => {
      usePwa.setState({ registration });
      const sub = await registration.pushManager?.getSubscription().catch(() => null);
      usePwa.setState({ endpoint: sub?.endpoint ?? null });
      registration.active?.postMessage({ type: 'flush' });
    })
    .catch(() => undefined);
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const p = usePwa.getState().installPrompt;
  if (!p) return 'unavailable';
  await p.prompt();
  const choice = await p.userChoice;
  usePwa.setState({ installPrompt: null });
  return choice.outcome;
}

async function readyRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = usePwa.getState().registration;
  if (existing) return existing;
  if (!('serviceWorker' in navigator)) throw new Error('This browser has no service workers.');
  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  usePwa.setState({ registration });
  return navigator.serviceWorker.ready;
}

/** Ask for permission, subscribe with the hub's VAPID key, and register this device with the hub. */
export async function enablePush(vapidPublicKey: string): Promise<void> {
  if (typeof Notification === 'undefined') throw new Error('This browser cannot show notifications.');
  const permission = await Notification.requestPermission();
  usePwa.setState({ permission });
  if (permission !== 'granted') {
    throw new ApiError({
      what: 'Notifications are blocked for Orgus.',
      why: permission === 'denied' ? 'This browser was told not to allow them.' : 'The permission prompt was dismissed.',
      next: 'Allow notifications for this site in the browser settings, then try again.',
    });
  }
  const reg = await readyRegistration();
  const key = urlBase64ToUint8Array(vapidPublicKey);
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with another hub's key cannot receive this hub's pushes.
  if (sub && sub.options.applicationServerKey && !sameKey(new Uint8Array(sub.options.applicationServerKey), key)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  const platform = detectPlatform(navigator.userAgent, navigator.maxTouchPoints);
  const json = sub.toJSON() as { endpoint: string; expirationTime?: number | null; keys: { p256dh: string; auth: string } };
  await subscribeDevice({ subscription: json, device: deviceName(navigator.userAgent, platform), mobile: platform !== 'desktop' });
  usePwa.setState({ endpoint: sub.endpoint });
}

export async function disablePush(): Promise<void> {
  const reg = usePwa.getState().registration ?? (await navigator.serviceWorker?.getRegistration());
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await unsubscribeDevice(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe();
  }
  usePwa.setState({ endpoint: null });
}

function sameKey(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** sha256 of the endpoint, first 16 hex chars: matches ChannelView.endpointHash. */
export async function endpointHash(endpoint: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
}

// ---------------------------------------------------------------------------
// The offline snapshot
// ---------------------------------------------------------------------------

export const SNAPSHOT_CACHE = 'airside-state-v1';
export const SNAPSHOT_URL = '/api/state';

/** The store's entities back in the GET /api/state shape. */
export function toAggregate(s: Entities): AggregateState {
  return {
    at: s.at,
    machines: Object.values(s.machines),
    projects: Object.values(s.projects),
    checkouts: Object.values(s.checkouts),
    sessions: Object.values(s.sessions),
    pipelines: Object.values(s.pipelines),
    jobs: Object.values(s.jobs),
    feed: s.feed.slice(0, 50),
    audit: [],
    suggestions: [],
    ext: s.ext,
  };
}

/** Keep the last state for offline viewing: the worker answers GET /api/state from it when the hub is unreachable. */
export async function saveSnapshot(s: Entities): Promise<void> {
  if (typeof caches === 'undefined' || !s.at) return;
  const cache = await caches.open(SNAPSHOT_CACHE);
  await cache.put(SNAPSHOT_URL, new Response(JSON.stringify(toAggregate(s)), { headers: { 'Content-Type': 'application/json' } }));
}
